import { useState } from "react";

import { Controller, useForm } from "react-hook-form";

import {
  CovSensorParamPeriodDtoType,
  CovSensorStatus,
  PlanningType,
  type PatchBuildingRoomsRequestBody,
} from "@eisox/api/core-api";
import { ActionButton, Alert, Button, cn, Select, Steps, Switch } from "@eisox/design-system";
import { isFeatureSupported } from "@eisox/device-features";
import { ArrowRightIcon, CrossIcon, HeatIcon, LeafIcon, PlusIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { roomApi } from "~/api";
import { FieldContainer } from "~/components/field-container";
import { PlanningsGenerationDialog } from "~/components/plannings-generation-dialog";
import { UnusualAbsenceDialog } from "~/components/unusual-absence-dialog";
import { VersionGate } from "~/components/version-gate";
import {
  frostFreePeriodsAllowed,
  FrostFreeSelect,
  getFrostFreeModes,
  getBuildingFrostFreeStatus,
} from "~/features/frost-free";
import { organizePlanningsByWeekDays, UpdatePlanningDialog, Week } from "~/features/planning";
import {
  booleanToSoundSensorParam,
  covSensorParamStatusOn,
  frostFreeMode,
  isSwitchEnabledOn,
  preComfortTimeslotGenerationOn,
  roomsFunctionSettingsSchema,
  RoomsSelector,
  soundSensorParamStatusOn,
  soundSensorParamToBoolean,
  unusualAbsenceDetectionOn,
  type Filters,
  type RoomsFunctionSettingsSchemaType,
} from "~/features/settings";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import type { EnhancedRoom } from "~/types/room.type";
import { getDirtyValues } from "~/utils/form.utils";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/rooms/functions")({
  component: RouteComponent,
});

function RouteComponent() {
  const { building, floors, rooms, gateways, valves } = useBuildingContext(Route.id);

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (body: PatchBuildingRoomsRequestBody[number]) => roomApi.update(building.id, [body]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
      reset(defaultValues);
      setFilters({});
    },
  });

  const [filters, setFilters] = useState<Filters>({});
  const [blured, setBlured] = useState(false);

  const defaultValues = {
    roomIds: [],
    mode: frostFreeMode([]),
    isSwitchEnabled: isSwitchEnabledOn([]),
    isAutoPrecomf: unusualAbsenceDetectionOn([]),
    isPreComfRoom: preComfortTimeslotGenerationOn([]),
    covSensorParam: {
      status: covSensorParamStatusOn([]),
      periods: [],
    },
    soundSensorParam: {
      status: soundSensorParamStatusOn([]),
    },
  };

  const {
    control,
    watch,
    formState: { isDirty, dirtyFields, errors },
    handleSubmit,
    reset,
  } = useForm({
    resolver: zodResolver(roomsFunctionSettingsSchema),
    defaultValues,
  });

  const roomIds = watch("roomIds");
  const mode = watch("mode");
  const covSensorParamStatus = watch("covSensorParam.status");

  const handleFilterRoomsChange = (rooms: EnhancedRoom[]) => {
    const roomIds = rooms.map(room => room.id);
    reset({
      roomIds: roomIds,
      mode: frostFreeMode(rooms),
      isSwitchEnabled: isSwitchEnabledOn(rooms),
      isAutoPrecomf: unusualAbsenceDetectionOn(rooms),
      isPreComfRoom: preComfortTimeslotGenerationOn(rooms),
      covSensorParam: {
        status: covSensorParamStatusOn(rooms),
        periods: [],
      },
      soundSensorParam: {
        status: soundSensorParamStatusOn(rooms),
      },
    });
  };

  const onSubmit = (data: RoomsFunctionSettingsSchemaType) => {
    const dirtyValues = getDirtyValues(dirtyFields, data);

    const body: PatchBuildingRoomsRequestBody[number] = {
      ids: data.roomIds,
      ...(dirtyValues.isSwitchEnabled != null && { isSwitchEnabled: dirtyValues.isSwitchEnabled }),
      ...(dirtyValues.isAutoPrecomf != null && { isAutoPrecomf: dirtyValues.isAutoPrecomf }),
      ...(dirtyValues.isPreComfRoom != null && { isPreComfRoom: dirtyValues.isPreComfRoom }),
      ...(dirtyValues.mode != null && {
        frostFree: { status: dirtyValues.mode },
      }),
      ...(dirtyValues.covSensorParam != null &&
        data.covSensorParam.status != null && {
          covSensorParam: {
            status: data.covSensorParam.status,
            periods: data.covSensorParam.periods.map(({ day, hourStart, hourEnd }) => ({
              type: CovSensorParamPeriodDtoType.enabled,
              day,
              hourStart,
              hourEnd,
            })),
          },
        }),
      ...(dirtyValues.soundSensorParam != null &&
        data.soundSensorParam.status != null && {
          soundSensorParam: { status: data.soundSensorParam.status },
        }),
    };

    mutate(body);
  };

  const isFrostFreePeriodsAllowed = frostFreePeriodsAllowed(gateways);
  const comfortTemperature = rooms.reduce((acc, room) => acc + room.comfortTemperature, 0) / rooms.length;

  const inputsDisabled = roomIds.length === 0;
  const submitDisabled = !isDirty || inputsDisabled || isPending;

  const roomsValves = valves.filter(v => roomIds.includes(v.roomId));

  const showSoundSensorFilter = rooms.some(room => {
    const roomValves = valves.filter(v => v.roomId === room.id);
    const roomGatewayIds = [...new Set(roomValves.map(v => v.gatewayId))];
    const roomGateways = gateways.filter(g => roomGatewayIds.includes(g.id));
    return (
      roomValves.length > 0 &&
      roomValves.every(v => isFeatureSupported("valve", "SOUND_SENSOR_PARAM", v.softwareVersion)) &&
      roomGateways.length > 0 &&
      roomGateways.every(g => isFeatureSupported("gateway", "SOUND_SENSOR_PARAM", g.softwareVersion))
    );
  });

  const showCovSensorFilter = rooms.some(room => {
    const roomValves = valves.filter(v => v.roomId === room.id);
    const roomGatewayIds = [...new Set(roomValves.map(v => v.gatewayId))];
    const roomGateways = gateways.filter(g => roomGatewayIds.includes(g.id));
    return (
      roomValves.length > 0 &&
      roomValves.every(v => isFeatureSupported("valve", "COV_SENSOR_PARAM", v.softwareVersion)) &&
      roomGateways.length > 0 &&
      roomGateways.every(g => isFeatureSupported("gateway", "COV_SENSOR_PARAM", g.softwareVersion))
    );
  });

  //* Remount switches when roomIds change to reset their state to normal switch if value is true or false
  const switchKey = roomIds.join("-");

  return (
    <>
      <form
        data-testid="RoomsFunctionSettings"
        className="flex h-full flex-col overflow-y-auto"
        onSubmit={e => void handleSubmit(onSubmit)(e)}
      >
        <Steps.Root>
          <Steps.Step>
            <Steps.Title>{m.select_places({ resource: "room" })}</Steps.Title>
            <Steps.Content>
              <FieldContainer classNames={{ container: "w-full" }} error={errors.roomIds}>
                <RoomsSelector.Root
                  rooms={rooms}
                  value={filters}
                  onValueChange={setFilters}
                  onFilteredRoomsChange={handleFilterRoomsChange}
                >
                  <div className="*:min-w-0 mb-1.25 gap-1.25 flex *:flex-1">
                    <RoomsSelector.TypeSearch />
                    <RoomsSelector.PlanSearch floors={floors} />
                    <RoomsSelector.GroupSearch />
                    <RoomsSelector.ParametersSearch
                      showSoundSensorFilter={showSoundSensorFilter}
                      showCovSensorFilter={showCovSensorFilter}
                      frostFreePeriodsAllowed={isFrostFreePeriodsAllowed}
                    />
                    <RoomsSelector.PersonnalizedSearch floors={floors} />
                  </div>
                  <RoomsSelector.Result />
                </RoomsSelector.Root>
              </FieldContainer>
            </Steps.Content>
          </Steps.Step>
          <Steps.Step>
            <Steps.Title>{m.update_settings()}</Steps.Title>
            <Steps.Content className="gap-2.5 flex flex-col min-[1200px]:flex-row">
              <div className="gap-2.5 flex flex-1 flex-col">
                <div
                  className={cn(
                    "max-w-105 gap-1.25 flex flex-col",
                    blured && !dirtyFields.isAutoPrecomf && "blur-[3px]",
                  )}
                >
                  <h3 className="font-semibold gap-1.25 leading-5.25 flex items-center text-base text-primary-blue">
                    {m.unusual_absence_detection()}
                    <LeafIcon color={colors.primary.green} />
                  </h3>
                  <Alert severity="help" className="flex">
                    <p className="font-normal text-base italic">
                      {m.unusual_absence_detection_help()}
                      <UnusualAbsenceDialog
                        absenceTemperature={building.absenceTemperature}
                        preComfortTemperature={building.preComfortTemperature}
                        comfortTemperature={comfortTemperature}
                      >
                        <Button className="gap-1.25 flex items-center text-base text-primary-blue">
                          {m.learn_more()}
                        </Button>
                      </UnusualAbsenceDialog>
                    </p>
                  </Alert>
                  <Controller
                    control={control}
                    name="isAutoPrecomf"
                    render={({ field: { value, onChange } }) => (
                      <FieldContainer
                        classNames={{
                          label: "italic font-normal text-base",
                          container: "flex flex-row justify-between items-center gap-[5px]",
                        }}
                        label={m.activate_in_certain_rooms({ value: `${value}` })}
                      >
                        <Switch key={switchKey} checked={value} onCheckedChange={onChange} disabled={inputsDisabled} />
                      </FieldContainer>
                    )}
                  />
                </div>
                <div
                  className={cn(
                    "max-w-105 gap-1.25 flex flex-col",
                    blured && !dirtyFields.isPreComfRoom && "blur-[3px]",
                  )}
                >
                  <h3 className="font-semibold gap-1.25 leading-5.25 flex items-center text-base text-primary-blue">
                    {m.pre_comfort_timeslot_generation()}
                    <HeatIcon color={colors.primary.orange} />
                  </h3>
                  <Alert severity="help" className="flex">
                    <p className="font-normal text-base italic">
                      {m.pre_comfort_timeslot_generation_during_unusual_absence()}
                      <PlanningsGenerationDialog
                        absenceTemperature={building.absenceTemperature}
                        preComfortTemperature={building.preComfortTemperature}
                        comfortTemperature={comfortTemperature}
                      >
                        <Button className="gap-1.25 flex items-center text-base text-primary-blue">
                          {m.learn_more()}
                        </Button>
                      </PlanningsGenerationDialog>
                    </p>
                  </Alert>
                  <Controller
                    control={control}
                    name="isPreComfRoom"
                    render={({ field: { value, onChange } }) => (
                      <FieldContainer
                        classNames={{
                          label: "italic font-normal text-base",
                          container: "flex flex-row justify-between items-center gap-[5px]",
                        }}
                        label={m.activate_in_certain_rooms({ value: `${value}` })}
                      >
                        <Switch key={switchKey} checked={value} onCheckedChange={onChange} disabled={inputsDisabled} />
                      </FieldContainer>
                    )}
                  />
                </div>
                <div
                  className={cn(
                    "max-w-105 gap-1.25 flex flex-col",
                    blured && !dirtyFields.isSwitchEnabled && "blur-[3px]",
                  )}
                >
                  <h3 className="font-semibold gap-1.25 leading-5.25 flex items-center text-base text-primary-blue">
                    {m.button_settings()}
                  </h3>
                  <Controller
                    control={control}
                    name="isSwitchEnabled"
                    render={({ field: { value, onChange } }) => (
                      <FieldContainer
                        classNames={{
                          label: "italic font-normal text-base",
                          container: "flex flex-row justify-between items-center gap-[5px]",
                        }}
                        label={m.activate_in_certain_rooms({ value: `${value}` })}
                      >
                        <Switch key={switchKey} checked={value} onCheckedChange={onChange} disabled={inputsDisabled} />
                      </FieldContainer>
                    )}
                  />
                </div>
                <div className={cn("max-w-105 gap-1.25 flex flex-col", blured && !dirtyFields.mode && "blur-[3px]")}>
                  <h3 className="font-semibold gap-1.25 leading-5.25 flex items-center text-base text-primary-blue">
                    {m.frost_free_mode()}
                  </h3>
                  {isFrostFreePeriodsAllowed ? (
                    <Button asChild>
                      <Link to={"/buildings/$buildingId/settings/rooms/holidays"} params={{ buildingId: building.id }}>
                        {m.see_parameters()}
                        <ArrowRightIcon />
                      </Link>
                    </Button>
                  ) : (
                    <>
                      <Controller
                        control={control}
                        name="mode"
                        render={({ field: { value, onChange } }) => (
                          <div className="gap-1.25 flex flex-row items-center">
                            <FrostFreeSelect
                              value={value}
                              onChange={onChange}
                              modes={getFrostFreeModes({ periodsAllowed: isFrostFreePeriodsAllowed })}
                              disabled={inputsDisabled}
                            />
                            {dirtyFields.mode && (
                              <CrossIcon
                                className="h-2.5 w-2.5 cursor-pointer text-primary-dark-gray"
                                onClick={() => onChange(undefined)}
                              />
                            )}
                          </div>
                        )}
                      />
                      {!mode && !inputsDisabled && (
                        <p className="font-normal text-base italic">{m.activate_in_certain_rooms({ value: "null" })}</p>
                      )}
                      {getBuildingFrostFreeStatus(building.frostFree).active && (
                        <Alert className="flex" severity="warning">
                          {m.building_frost_free_mode_activated()}
                          <Link
                            className="gap-1.25 flex items-center text-base text-primary-blue"
                            to={"/buildings/$buildingId/settings/buildings/holidays"}
                            params={{ buildingId: building.id }}
                          >
                            {m.see_parameter()}
                            <ArrowRightIcon />
                          </Link>
                        </Alert>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="gap-2.5 flex flex-1 flex-col">
                <div
                  className={cn(
                    "max-w-105 gap-1.25 flex flex-col",
                    blured && !dirtyFields.soundSensorParam && "blur-[3px]",
                  )}
                >
                  <h3 className="font-semibold gap-1.25 leading-5.25 flex items-center text-base text-primary-blue">
                    {m.sound_sensor()}
                  </h3>
                  <Alert severity="help" className="flex">
                    <p className="font-normal text-base italic">{m.sound_sensor_param_help()}</p>
                  </Alert>

                  <Controller
                    control={control}
                    name="soundSensorParam.status"
                    render={({ field: { value, onChange } }) => (
                      <VersionGate
                        devices={[
                          {
                            type: "valve",
                            current: roomsValves.map(v => v.softwareVersion),
                            feature: "SOUND_SENSOR_PARAM",
                          },
                          {
                            type: "gateway",
                            current: roomsValves.map(v => v.gatewaySoftwareVersion),
                            feature: "SOUND_SENSOR_PARAM",
                          },
                        ]}
                      >
                        {({ disabled }) => (
                          <FieldContainer
                            classNames={{
                              label: "italic font-normal text-base",
                              container: "flex flex-row justify-between items-center gap-[5px]",
                            }}
                            label={m.activate_in_certain_rooms({ value: `${soundSensorParamToBoolean(value)}` })}
                          >
                            <Switch
                              key={switchKey}
                              checked={soundSensorParamToBoolean(value)}
                              onCheckedChange={checked => onChange(booleanToSoundSensorParam(checked))}
                              disabled={inputsDisabled || disabled}
                            />
                          </FieldContainer>
                        )}
                      </VersionGate>
                    )}
                  />
                </div>

                <div
                  className={cn(
                    "max-w-105 gap-1.25 flex flex-col",
                    blured && !dirtyFields.covSensorParam && "blur-[3px]",
                  )}
                >
                  <h3 className="font-semibold gap-1.25 leading-5.25 flex items-center text-base text-primary-blue">
                    {m.air_sensor()}
                  </h3>

                  <Controller
                    control={control}
                    name="covSensorParam.status"
                    render={({ field: { value, onChange } }) => (
                      <Select.Root multiple={false} value={value?.toString()} onValueChange={onChange}>
                        <VersionGate
                          devices={[
                            {
                              type: "valve",
                              current: roomsValves.map(v => v.softwareVersion),
                              feature: "COV_SENSOR_PARAM",
                            },
                            {
                              type: "gateway",
                              current: roomsValves.map(v => v.gatewaySoftwareVersion),
                              feature: "COV_SENSOR_PARAM",
                            },
                          ]}
                        >
                          {({ disabled }) => (
                            <Select.Trigger disabled={inputsDisabled || disabled}>
                              <Select.Value placeholder={m.chose_cov_sensor_mode()} />
                            </Select.Trigger>
                          )}
                        </VersionGate>
                        <Select.Content>
                          <Select.Viewport>
                            <Select.Item key={CovSensorStatus.ENABLED} value={CovSensorStatus.ENABLED}>
                              {m.activation({ activation: "true" })}
                            </Select.Item>
                            <Select.Item key={CovSensorStatus.DISABLED} value={CovSensorStatus.DISABLED}>
                              {m.activation({ activation: "false" })}
                            </Select.Item>
                            <Select.Item key={CovSensorStatus.PERIODS} value={CovSensorStatus.PERIODS}>
                              {m.activate_by_timeslot()}
                            </Select.Item>
                            <Select.Item key={CovSensorStatus.PRESENCE} value={CovSensorStatus.PRESENCE}>
                              {m.activate_on_presence_timeslot()}
                            </Select.Item>
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Root>
                    )}
                  />

                  {covSensorParamStatus === CovSensorStatus.PRESENCE && (
                    <Alert severity="help">{m.cov_sensor_param_help()}</Alert>
                  )}
                  {covSensorParamStatus === CovSensorStatus.PERIODS && (
                    <Controller
                      control={control}
                      name="covSensorParam.periods"
                      render={({ field: { value, onChange } }) => {
                        const { reorderedDays, timeslotsByDay } = organizePlanningsByWeekDays(value);
                        return (
                          <div className="gap-2.5 flex flex-1 flex-col">
                            <div className="[&_span]:font-medium gap-2.5 flex items-center justify-between [&_span]:text-base">
                              <span>{m.planning()}</span>
                              <UpdatePlanningDialog
                                userDefinedPlannings={value.map(v => ({ ...v, userDefined: true }))}
                                types={[PlanningType.ENABLED]}
                                onValueChange={v => Promise.resolve(onChange(v))}
                              >
                                <Button disabled={value.length >= 14}>
                                  <PlusIcon />
                                </Button>
                              </UpdatePlanningDialog>
                            </div>
                            <Week.Root className="flex-1">
                              {reorderedDays.map(day => (
                                <Week.Day key={day} day={day as 0 | 1 | 2 | 3 | 4 | 5 | 6} className="min-h-62.5">
                                  {(timeslotsByDay[day] ?? []).map((timeslot, index) => (
                                    <UpdatePlanningDialog
                                      key={index}
                                      userDefinedPlannings={value.map(v => ({ ...v, userDefined: true }))}
                                      types={[PlanningType.ENABLED]}
                                      value={{ ...timeslot, userDefined: true }}
                                      onValueChange={v => Promise.resolve(onChange(v))}
                                    >
                                      <Week.Timeslot
                                        key={index}
                                        start={timeslot.hourStart}
                                        end={timeslot.hourEnd}
                                        type={timeslot.type}
                                        userDefined
                                      />
                                    </UpdatePlanningDialog>
                                  ))}
                                </Week.Day>
                              ))}
                            </Week.Root>
                          </div>
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            </Steps.Content>
          </Steps.Step>
          <Steps.Step>
            <Steps.Title>{m.apply_parameters()}</Steps.Title>
            <Steps.Content className="gap-2.5 flex flex-col items-start">
              {rooms.length === roomIds.length && isDirty && <Alert severity="warning">{m.updates_not_saved()}</Alert>}
              <ActionButton
                type="submit"
                disabled={submitDisabled}
                onMouseEnter={() => setBlured(isDirty)}
                onMouseLeave={() => setBlured(false)}
              >
                {m.save()}
                <ArrowRightIcon />
              </ActionButton>
            </Steps.Content>
          </Steps.Step>
        </Steps.Root>
      </form>
    </>
  );
}
